<template>
  <div v-if="document">
    <popup-add-data-controller ref="popupAddDataController" />
    <popup-select-revision ref="popupSelectRevision" />
    <vx-card no-shadow>
      <vs-breadcrumb class="mt-2">
        <li>
          <router-link to="/crm/legaldocuments"> Legal documents</router-link
          ><span class="vs-breadcrum--separator">/</span>
        </li>
        <li aria-current="page" class="active">Edit document</li>
      </vs-breadcrumb>

      <div class="vx-row mb-6 mt-2">
        <div class="vx-col sm:w-2/5 w-full">
          <h4>
            {{ document.name }}
            <small
              ><a
                v-if="!isRevision"
                style="color: #7367f0"
                @click="
                  $refs.popupSelectRevision.show(
                    document.id,
                    document.revisionDates
                  )
                "
                class="cursor-pointer"
                >(Revision {{ revision }})</a
              ><span v-else>(Revision {{ revision }})</span></small
            >
          </h4>
        </div>
      </div>

      <p v-if="isRevision" class="mb-6">
        You are viewing an older revision. This cannot be updated.
        <a @click="cancelRevisionView" class="cursor-pointer"
          >Click here to go back</a
        >
      </p>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Enabled</span>
        </div>
        <div class="vx-col">
          <vs-switch
            color="success"
            :disabled="isRevision"
            v-model="document.enabled"
          />
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Name</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-input
            label=""
            :disabled="isRevision"
            v-model="document.name"
            class="w-full"
          />
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Language</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-select
            class="w-full"
            :disabled="isRevision"
            v-model="document.language"
          >
            <vs-select-item
              :key="index"
              :value="item.value"
              :text="item.name"
              v-for="(item, index) in languages"
            />
          </vs-select>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Countries</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-select
            class="w-full"
            :disabled="isRevision"
            multiple
            v-model="document.countries"
          >
            <vs-select-item
              :key="index"
              :value="item.value"
              :text="item.name"
              v-for="(item, index) in countries"
            />
          </vs-select>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Consent types</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-select
            class="w-full"
            :disabled="isRevision"
            multiple
            v-model="consents"
          >
            <vs-select-item
              :key="index"
              :value="item"
              :text="consentNames[index]"
              v-for="(item, index) in consentTypes"
            />
          </vs-select>
        </div>
      </div>

      <div class="vx-row mb-6">
        <div class="vx-col sm:w-1/5 w-full">
          <span>Data controllers</span>
        </div>
        <div class="vx-col sm:w-2/5 w-full">
          <vs-row class="w-full">
            <vs-select
              :disabled="isRevision"
              style="width: 90%"
              multiple
              v-model="dataControllers"
            >
              <vs-select-item
                :key="index"
                :value="item.id"
                :text="item.name + ' (' + item.vat + ')'"
                v-for="(item, index) in controllers"
              />
            </vs-select>
            <vs-button
              radius
              :disabled="isRevision"
              color="primary"
              type="border"
              class="my-1 mx-2"
              size="small"
              icon="add"
              @click="$refs.popupAddDataController.show()"
            ></vs-button>
          </vs-row>
        </div>
      </div>

      <div id="toolbar">
        <span class="ql-formats">
          <button class="ql-list" value="ordered"></button>
          <button class="ql-list" value="bullet"></button>
        </span>
        <span class="ql-formats">
          <select class="ql-header" defaultValue="">
            <option value="1"></option>
            <option value="2"></option>
            <option value="3"></option>
            <option value=""></option>
          </select>
          <select class="ql-font" defaultValue=""></select>
          <select class="ql-size" defaultValue="">
            <option value="small"></option>
            <option value=""></option>
            <option value="large"></option>
            <option value="huge"></option>
          </select>
        </span>
        <span class="ql-formats">
          <button class="ql-indent" value="-1"></button>
          <button class="ql-indent" value="+1"></button>
        </span>
        <span class="ql-formats">
          <button class="ql-bold"></button>
          <button class="ql-italic"></button>
          <button class="ql-underline"></button>
          <button class="ql-strike"></button>
          <button class="ql-blockquote"></button>
          <button class="ql-code-block"></button>
        </span>
        <span class="ql-formats">
          <select class="ql-placeholder">
            <option v-for="ph in placeholders" :key="ph.id" :value="ph.label">
              {{ ph.name }}
            </option>
          </select>
        </span>
      </div>
      <quill-editor
        :disabled="isRevision"
        class="editor"
        v-model="documentHtml"
        :options="editorOption"
      ></quill-editor>

      <!-- Save & Remove Button -->
      <vs-row
        vs-justify="space-between"
        class="flex flex-wrap items-center"
        style="margin-top: 32px"
      >
        <vs-button
          id="remove-button"
          v-if="$AuthService.hasPermission('vp.crm.legaldocuments.edit')"
          @click="confirmRemove"
          color="danger"
          class="mt-2"
          :disabled="isRevision"
          >Remove</vs-button
        >
        <vs-button
          id="save-button"
          v-if="$AuthService.hasPermission('vp.crm.legaldocuments.edit')"
          @click="update"
          :disabled="isRevision"
          class="mt-2"
          >Save Changes</vs-button
        >
      </vs-row>
    </vx-card>

    <vx-card no-shadow> </vx-card>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import moment from "moment";

import PopupAddDataController from "./PopupAddDataController.vue";
import PopupSelectRevision from "./PopupSelectRevision.vue";

import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";

import "quill-mention";

import { quillEditor } from "vue-quill-editor";

import Quill from "quill";
import PlaceholderModule from "quill-placeholder-module";
Quill.register("modules/placeholder", PlaceholderModule(Quill));

export default {
  components: { quillEditor, PopupAddDataController, PopupSelectRevision },
  props: {},
  data() {
    return {
      moment,
      documentHtml: "",
      dataControllers: [],
      consents: [],
      documentId: "",
      revision: undefined,
    };
  },
  methods: {
    cancelRevisionView() {
      this.$router.go(-1);
    },
    updateFromDoc() {
      this.revision = this.$route.params.revision
        ? this.$route.params.revision
        : this.document.version;
      this.documentHtml = this.isRevision
        ? this.document.revisions[this.revision]
        : this.document.documentHtml;
      this.dataControllers = this.isRevision
        ? this.document.revisionDataControllers[this.revision]
        : this.document.dataControllers;
      this.consents = this.isRevision
        ? this.document.revisionConsents[this.revision]
        : this.document.consents;
    },
    async update() {
      this.$vs.loading();

      try {
        const dto = {
          ...this.document,
          documentHtml: this.documentHtml,
          dataControllers: this.dataControllers,
          consents: this.consents,
        };
        console.log("Updating document", dto);
        await this.$store.dispatch("legalDocuments/update", dto);

        this.updateFromDoc();
        this.$vs.notify({
          color: "green",
          title: "Legal document saved",
          text: "The legal document was successfully saved",
        });
      } catch (err) {
        console.error(err);

        this.$vs.notify({
          color: "danger",
          title: "Legal document not saved",
          text: "Legal document could not be saved",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async confirmRemove() {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `Confirm`,
        text: "Are you sure you want to delete this legal document?",
        accept: this.remove,
      });
    },
    async remove() {
      this.$vs.loading();
      await this.$store.dispatch("legalDocuments/delete", this.documentId);
      this.$vs.loading.close();
      this.$router.go(-1);
    },
  },
  computed: {
    isRevision() {
      return !!this.$route.params.revision;
    },
    ...mapGetters({
      consentTypes: "guestProfiles/consentTypes",
      consentNames: "guestProfiles/consentNames",
      controllers: "legalDocuments/controllers",
      countries: "legalDocuments/countries",
      languages: "legalDocuments/languages",
    }),
    editorOption() {
      return {
        modules: {
          toolbar: { container: `#toolbar` },
          placeholder: {
            delimiters: ["{{", "}}"], // default
            placeholders: this.placeholders,
          },
        },
        placeholder: "",
        theme: "snow", // or 'bubble'
      };
    },
    placeholders() {
      return this.$store.getters["handlebars/getFor"](
        "venue",
        "dataControllers",
        "dataProcessor",
        "legalDocument"
      ).map((e) => {
        return {
          id: e.handlebar,
          name: e.name,
          label: e.handlebar,
        };
      });
    },
    document() {
      return this.$store.getters["legalDocuments/document"](this.documentId);
    },
  },
  async created() {
    this.documentId = this.$route.params.id;
    await this.$store.dispatch("legalDocuments/getAll", false);
    await this.$store.dispatch("legalDocuments/getAllControllers", false);
    this.updateFromDoc();
  },
};
</script>

<style lang="scss">
.ql-picker.ql-placeholder {
  width: 118px;
}

.ql-picker.ql-placeholder > span.ql-picker-label::before {
  content: "Placeholders";
}

.ql-picker.ql-placeholder
  > span.ql-picker-options
  > span.ql-picker-item::before {
  content: attr(data-label);
}
</style>